import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { DensityContext } from '@/stores/DensityContext';
import {
  loadFichaHeaderCompact,
  loadViewDensity,
  saveFichaHeaderCompact,
  saveViewDensity,
  type ViewDensity,
} from '@/utils/sessionSectionPrefs';
import { TopbarSlotProvider } from './TopbarSlot';
import { FloatingChat } from './FloatingChat';
import { DayAgendaDrawer } from './DayAgendaDrawer';
import { AgendaDockProvider, useAgendaDock, type DockView } from './AgendaDockContext';
import { useAuth } from '@/hooks/useAuth';
import { useTour } from '@/hooks/useTour';
import { TourOverlay } from '@/components/tour/TourOverlay';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { BriefingAlertSound } from '@/components/alerts/BriefingAlertSound';
import { CallAlertWatcher } from '@/components/alerts/CallAlertWatcher';
import { NextPatientWatcher } from '@/components/alerts/NextPatientWatcher';
import { CallNextWatcher } from '@/components/alerts/CallNextWatcher';
import { CallNextConfirm } from '@/components/alerts/CallNextConfirm';
import { MuralAlertWatcher } from '@/components/alerts/MuralAlertWatcher';
import { RecordingProvider } from '@/pages/audio-ai/RecordingContext';
import { ExtractionProvider } from '@/pages/audio-ai/ExtractionContext';
import { RecordingIndicatorPill } from '@/pages/audio-ai/RecordingIndicatorPill';
import { FloatingRecordingButton } from './FloatingRecordingButton';
import { FloatingDictateButton } from './FloatingDictateButton';
import { FloatingCameraButton } from './FloatingCameraButton';
import { ClinicalCameraProvider } from '@/components/camera/ClinicalCameraContext';
import { DictationProvider } from '@/components/voice/DictationContext';
import { DictationHotkey } from '@/components/voice/DictationHotkey';
import { useVoiceDockPx } from '@/components/voice/voiceDockWidth';
import { AMPLO_MIN_CONTENT_PX, setContentWidth } from './contentWidth';
import { WakeWordWatcher } from '@/components/voice/WakeWordWatcher';
import { WakeStatusChip } from '@/components/voice/WakeStatusChip';
import { VoiceCommandWatcher } from '@/components/voice/VoiceCommandWatcher';
import { AtlasGlow } from '@/components/voice/AtlasGlow';
import { FocusModeContext } from '@/stores/FocusModeContext';
import { useEvolucaoVisual } from '@/utils/evolucaoVisual';

/** Largura do menu lateral expandido (w-64) — usada pra estimar o espaço que
 *  sobra pro conteúdo quando o painel direito abre. */
const SIDEBAR_EXPANDED_PX = 256;
/** Menu recolhido em trilho: 88px no visual novo, 64 no antigo — vale o maior,
 *  pra errar pro lado de abrir espaço. */
const SIDEBAR_COLLAPSED_PX = 88;
/** Abaixo disto de conteúdo útil, a sidebar recolhe sozinha enquanto o painel
 *  direito ou o ditado estiverem abertos (a ficha do paciente precisa de
 *  ~1000px pra caber no Amplo). */
const AUTO_COLLAPSE_MIN_CONTENT = AMPLO_MIN_CONTENT_PX;
/** Alt + esta tecla física alterna o modo foco (Alt+A agenda, Alt+R resumo,
 *  Alt+C chat — Alt+F/E/D são do Chrome). */
const FOCUS_TOGGLE_CODE = 'KeyZ';

/**
 * Coluna de conteúdo (Topbar + página). Reserva à direita o espaço que a
 * agenda encaixada está ocupando (`dockWidth` vindo do AgendaDockContext),
 * encolhendo o conteúdo pra ele ficar AO LADO da gaveta — nunca coberto.
 * "0px" = agenda fechada/sobreposta, então ocupa tudo como antes.
 */
const MainColumn = ({
  onMenuClick,
  onDesktopToggle,
  isDesktopCollapsed,
}: {
  onMenuClick: () => void;
  onDesktopToggle: () => void;
  isDesktopCollapsed: boolean;
}) => {
  const { dockWidth } = useAgendaDock();
  // Mede o que sobra pra página (sem o espaço da agenda encaixada) — é isso,
  // e não a janela, que decide se o Amplo da ficha cabe (`contentWidth.ts`).
  const colRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = colRef.current;
    if (!el) return;
    const medir = () => {
      const padRight = parseFloat(getComputedStyle(el).paddingRight) || 0;
      setContentWidth(Math.round(el.clientWidth - padRight));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setContentWidth(null);
    };
  }, []);
  return (
    <div
      ref={colRef}
      className="flex-1 flex flex-col min-w-0 overflow-hidden transition-[padding] duration-300 ease-out"
      style={{ paddingRight: dockWidth }}
    >
      <Topbar
        onMenuClick={onMenuClick}
        onDesktopToggle={onDesktopToggle}
        isDesktopCollapsed={isDesktopCollapsed}
      />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
        {/* Erros de render numa página não devem desmontar Sidebar/Topbar.
            Sem isso, um throw em qualquer página (ex.: data corrompida no
            modal de "Próx. vaga") produz tela preta. */}
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
};

export const Shell = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const { startTour, hasCompletedTour } = useTour();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // ── Densidade = estado da sidebar (um controle só) ──
  // O botão de recolher o menu lateral É o toggle de densidade: menu
  // recolhido → 'compacto' (cabeçalhos colapsam, paddings apertam, editor
  // ganha altura); expandido → 'conforto'. Preferência salva por usuário;
  // em tela de notebook pequeno o padrão já nasce recolhido.
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(
    () => loadViewDensity(user?.uid) === 'compacto',
  );
  useEffect(() => {
    setIsDesktopCollapsed(loadViewDensity(user?.uid) === 'compacto');
  }, [user?.uid]);

  useEffect(() => {
    if (!loading && isAuthenticated && !hasCompletedTour) {
      startTour();
    }
  }, [loading, isAuthenticated, hasCompletedTour, startTour]);

  if (loading) {
    return <div className="min-h-screen bg-surface flex flex-col items-center justify-center">Loading...</div>;
  }

  // If not authenticated, the ProtectedRoute component handles the redirect.
  // Shell is only rendered inside Protected routes typically.

  return (
    <RecordingProvider>
      <ExtractionProvider>
      <DictationProvider>
      <ClinicalCameraProvider>
      <AgendaDockProvider>
        <ShellBody
          uid={user?.uid}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isDesktopCollapsed={isDesktopCollapsed}
          setIsDesktopCollapsed={setIsDesktopCollapsed}
        />
      </AgendaDockProvider>
      </ClinicalCameraProvider>
      </DictationProvider>
      </ExtractionProvider>
    </RecordingProvider>
  );
};

/**
 * Miolo do Shell — vive DENTRO do AgendaDockProvider pra enxergar o painel
 * direito. É daqui que saem os três comandos de tela (menu, laterais, foco)
 * publicados no FocusModeContext. Também trata o painel aberto numa tela
 * apertada: a sidebar recolhe sozinha (sem gravar preferência) até o painel
 * fechar ou o médico expandir à mão.
 */
const ShellBody = ({
  uid,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isDesktopCollapsed,
  setIsDesktopCollapsed,
}: {
  uid: string | undefined;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (v: boolean) => void;
  isDesktopCollapsed: boolean;
  setIsDesktopCollapsed: (v: boolean) => void;
}) => {
  const { open: dockOpen, view: dockView, dockWidth, closeDock, openView } = useAgendaDock();

  // ── Recolher/expandir "as laterais" (menu esquerdo + painel direito) ──
  // Usado pelo botão das laterais e pelo modo foco. Ao expandir, o painel
  // direito volta na vista em que estava quando foi recolhido.
  const restoreDockRef = useRef<DockView | null>(null);
  /** Vista da agenda que o ditado recolheu ao abrir (volta quando ele fecha). */
  const voiceHidDockRef = useRef<DockView | null>(null);
  const dockRef = useRef({ open: dockOpen, view: dockView, closeDock, openView });
  dockRef.current = { open: dockOpen, view: dockView, closeDock, openView };
  const collapseSides = useCallback(() => {
    const d = dockRef.current;
    if (d.open) restoreDockRef.current = d.view;
    // Recolheu as laterais à mão: fechar o ditado não reabre a agenda.
    voiceHidDockRef.current = null;
    d.closeDock();
  }, []);
  const restoreSides = useCallback(() => {
    const view = restoreDockRef.current;
    restoreDockRef.current = null;
    // Expandir as laterais ABRE o painel mesmo que ele estivesse fechado antes
    // de recolher — senão o botão "expandir" não teria efeito visível à
    // direita. Volta na vista de antes; sem histórico, cai na agenda.
    if (!dockRef.current.open) dockRef.current.openView(view ?? 'agenda');
  }, []);

  // Largura da janela — pra saber quanto sobra pro conteúdo com o painel aberto.
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Recolhe a sidebar automaticamente quando o painel direito ou a coluna do
  // ditado deixam o conteúdo estreito demais. "Dispensado" = o médico
  // expandiu à mão enquanto eles estavam abertos; volta a valer quando fecham.
  const reservedPx = parseInt(dockWidth, 10) || 0;
  const voicePx = useVoiceDockPx();
  const tight =
    (dockOpen || voicePx > 0) &&
    vw - SIDEBAR_EXPANDED_PX - reservedPx - voicePx < AUTO_COLLAPSE_MIN_CONTENT;
  const [autoDismissed, setAutoDismissed] = useState(false);
  useEffect(() => {
    if (!dockOpen && voicePx === 0) setAutoDismissed(false);
  }, [dockOpen, voicePx]);
  const autoCollapsed = tight && !autoDismissed;

  // ── Ditar abriu com a agenda aberta e a ficha ficaria apertada ──
  // A agenda recolhe pro trilho enquanto a coluna do ditado estiver aberta e
  // volta, na mesma vista, quando ela fecha — a ficha ganha ~390px e o Amplo
  // continua cabendo. Abrir a agenda à mão no meio do ditado vale: ela fica,
  // e fechar o ditado não mexe mais nela.
  const prevVoicePxRef = useRef(voicePx);
  useEffect(() => {
    const antes = prevVoicePxRef.current;
    prevVoicePxRef.current = voicePx;
    const d = dockRef.current;
    // Abriu ou ALARGOU (coluna larga do Ao vivo): a agenda recolhe se apertar.
    if (voicePx > antes) {
      const sobra = vw - SIDEBAR_COLLAPSED_PX - reservedPx - voicePx;
      if (d.open && sobra < AUTO_COLLAPSE_MIN_CONTENT) {
        voiceHidDockRef.current = d.view;
        d.closeDock();
      }
    } else if (antes > 0 && voicePx === 0) {
      const vista = voiceHidDockRef.current;
      voiceHidDockRef.current = null;
      if (vista && !d.open) d.openView(vista);
    }
  }, [voicePx, vw, reservedPx]);
  useEffect(() => {
    if (dockOpen) voiceHidDockRef.current = null;
  }, [dockOpen]);

  const effectiveCollapsed = isDesktopCollapsed || autoCollapsed;
  const density: ViewDensity = effectiveCollapsed ? 'compacto' : 'conforto';
  const setDensity = useCallback(
    (d: ViewDensity) => {
      saveViewDensity(uid, d);
      setIsDesktopCollapsed(d === 'compacto');
    },
    [uid, setIsDesktopCollapsed],
  );
  // ── Botão 1: só o menu da esquerda ──
  const onDesktopToggle = useCallback(() => {
    if (effectiveCollapsed && !isDesktopCollapsed) {
      // Recolhida só pelo automático: expandir à mão dispensa o automático
      // nesta abertura, sem mexer na preferência salva.
      setAutoDismissed(true);
      return;
    }
    setDensity(isDesktopCollapsed ? 'conforto' : 'compacto');
  }, [effectiveCollapsed, isDesktopCollapsed, setDensity]);

  // ── Botão 2: as duas laterais juntas (menu + painel da agenda) ──
  const sidesCollapsed = effectiveCollapsed && !dockOpen;
  const setSides = useCallback(
    (collapse: boolean) => {
      setDensity(collapse ? 'compacto' : 'conforto');
      if (collapse) {
        collapseSides();
      } else {
        // Expandir à mão também dispensa o recolhimento automático.
        setAutoDismissed(true);
        restoreSides();
      }
    },
    [setDensity, collapseSides, restoreSides],
  );
  const toggleSides = useCallback(() => setSides(!sidesCollapsed), [setSides, sidesCollapsed]);

  // ── Cabeçalho da ficha numa linha só (preferência salva por usuário) ──
  // Mora aqui, e não na ficha, pra que o modo foco possa recolhê-lo junto —
  // a ficha lê e alterna pelo mesmo contexto, com botão próprio.
  // Nova evolução em Linhas/Abas (spec 026): sem escolha salva, nasce recolhido.
  const cabecalhoUmaLinha = useEvolucaoVisual() !== 'cartoes';
  const [headerCompact, setHeaderCompactState] = useState(() => loadFichaHeaderCompact(uid, cabecalhoUmaLinha));
  useEffect(() => {
    setHeaderCompactState(loadFichaHeaderCompact(uid, cabecalhoUmaLinha));
  }, [uid, cabecalhoUmaLinha]);
  const setHeaderCompact = useCallback((v: boolean) => {
    saveFichaHeaderCompact(uid, v);
    setHeaderCompactState(v);
  }, [uid]);
  const toggleHeaderCompact = useCallback(
    () => setHeaderCompact(!headerCompact),
    [headerCompact, setHeaderCompact],
  );

  // ── Botão 3 (foco, Alt+Z): laterais + cabeçalho num clique ──
  // Derivado: nada é "forçado", então os outros botões continuam valendo
  // enquanto ele estiver ativo (era esse o bug do menu que não expandia).
  const focus = sidesCollapsed && headerCompact;
  const toggleFocus = useCallback(() => {
    const collapse = !focus;
    setSides(collapse);
    setHeaderCompact(collapse);
  }, [focus, setSides, setHeaderCompact]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === FOCUS_TOGGLE_CODE) {
        e.preventDefault();
        toggleFocus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleFocus]);

  return (
      <FocusModeContext.Provider
        value={{ focus, toggleFocus, sidesCollapsed, toggleSides, headerCompact, toggleHeaderCompact }}
      >
      <DensityContext.Provider value={{ density, setDensity }}>
      {/* `--voice-dock-w`: a coluna "Ditar documentos" aberta reserva a
          largura dela à esquerda (VoiceSidePanel) — a tela desliza em vez
          de ficar coberta. Sem ditado, 0px. */}
      <div
        className="flex h-screen bg-surface overflow-hidden transition-[padding] duration-300 ease-out"
        style={{ paddingLeft: 'var(--voice-dock-w, 0px)' }}
      >
        <TourOverlay />
        <Sidebar
          isMobileOpen={isMobileMenuOpen}
          onCloseMobile={() => setIsMobileMenuOpen(false)}
          isDesktopCollapsed={effectiveCollapsed}
        />
        <TopbarSlotProvider>
          <MainColumn
            onMenuClick={() => setIsMobileMenuOpen(true)}
            onDesktopToggle={onDesktopToggle}
            isDesktopCollapsed={effectiveCollapsed}
          />
        </TopbarSlotProvider>
        <FloatingChat />
        <DayAgendaDrawer />
        <BriefingAlertSound />
        <CallAlertWatcher />
        <NextPatientWatcher />
        <CallNextWatcher />
        {/* Lado do médico: "Recepção avisada" → "Secretária deu ciente". */}
        <CallNextConfirm />
        <MuralAlertWatcher />
        {/* Ditado sem clique (spec 011): Alt+V/pedal e a escuta do "Jarvis". */}
        <DictationHotkey />
        <WakeWordWatcher />
        {/* "Atlas, manda entrar" / "abre a agenda" (comandos do sistema). */}
        <VoiceCommandWatcher />
        {/* Brilho do "Atlas" nas bordas da tela (estilo Siri). */}
        <AtlasGlow />
        {/* Pílula flutuante da gravação — só aparece no mobile com o
            drawer fechado. No desktop e com drawer aberto, o estado de
            gravação já é mostrado pelo SidebarRecordingButton; o pill
            seria duplicação. md:hidden no wrapper garante o esconde no
            desktop mesmo que o flag JS falhe. */}
        {!isMobileMenuOpen && (
          <div className="md:hidden">
            <RecordingIndicatorPill />
          </div>
        )}
        {/* Botão flutuante "Gravar áudio" — aparece no mobile quando o
            menu lateral está fechado. No desktop, o trigger vive dentro
            da sidebar (ver SidebarRecordingButton). */}
        <FloatingRecordingButton hidden={isMobileMenuOpen} />
        {/* Pilha acima do de gravação (que mora em bottom-4): ditado embaixo,
            câmera em cima. Quem não aparece não deixa buraco. */}
        <div className="md:hidden fixed bottom-20 left-4 z-40 flex flex-col-reverse gap-4">
          <FloatingDictateButton hidden={isMobileMenuOpen} />
          <FloatingCameraButton hidden={isMobileMenuOpen} />
          {/* Escuta "Jarvis"/"Atlas" no celular (spec 024). */}
          {!isMobileMenuOpen && <WakeStatusChip flutuante />}
        </div>
      </div>
      </DensityContext.Provider>
      </FocusModeContext.Provider>
  );
};
